import * as R from 'ramda';
import { executeSql } from '../database';
import uuid from 'uuid/v1';
import { now } from '../../../utils';
import { api, apiConfig } from 'components/api';
import async from 'async-es';
import { downloadData, downloadZipData, fileSystem, sendData } from 'components/fileSystem';
import * as Promise from 'bluebird';
import * as moment from 'moment';

const modelFieldCheckType = modelFields => field => type => modelFields[field].type === type;

const modelFieldDefaultValue = modelFields => field => modelFields[field].default || '';

const modelFieldDefaultValueBool = modelFields => field => modelFields[field].default || false;

const getModelKeys = modelFields => R.keys(modelFields);

const getBool = val => {
  var num = +val;
  return !isNaN(num)
    ? !!num
    : !!String(val)
        .toLowerCase()
        .replace(!!0, '');
};

const proccessModelResultFields = (model, cache = {}) => object =>
  new Promise((success, reject) => {
    if (!model) {
      reject('Model must not be undefined');
    } else {
      const result = R.pipe(
        R.pick(getModelKeys(model.fields)),
        R.mapObjIndexed((value, field) => {
          if (field === 'updated_at' || field === 'created_at') {
            return typeof value === 'string' ? moment(value).format('x') * 1 : value;
          }

          if (modelFieldCheckType(model.fields)(field)('array') || modelFieldCheckType(model.fields)(field)('object')) {
            try {
              return JSON.parse(object[field]);
            } catch (e) {
              console.log(e.message);
              return;
            }
          } else if (modelFieldCheckType(model.fields)(field)('raw-picture')) {
            if (!cache[result[model.id]]) {
              cache[result[model.id]] = {};
            }
            cache[result[model.id]][field] = value;
            return 'RAWPICTURE';
          } else if (modelFieldCheckType(model.fields)(field)('boolean')) {
            return getBool(R.defaultTo(modelFieldDefaultValueBool(model.fields)(field))(value));
          }
          return R.defaultTo(modelFieldDefaultValue(model.fields)(field))(value);
        })
      )(object);

      success(result);
    }
  });

export const datasource = (databaseAlias, currentUserId, actions = {}) => (tableName, schema) =>
  new Promise(datasourceSuccess => {
    const executeDBSql = executeSql(databaseAlias);

    const datasourceActions = R.merge({
      create: () => console.info(`${tableName}: Creating datasource.`),
      created: () => console.info(`${tableName}: Datasource created.`),
      localSyncStart: () => console.info(`${tableName}: Local sync start.`),
      localSyncProgress: () => console.info(`${tableName}: Local sync start.`),
      localSyncEnd: () => console.info(`${tableName}: Local sync start.`),
      remoteSyncStart: () => console.info(`${tableName}: Remote sync start.`),
      remoteSyncProgress: percentage => console.info(`${tableName}: Remote sync start. ${percentage}`),
      remoteSyncEnd: () => console.info(`${tableName}: Remote sync start.`),
      localCreateStart: () => console.info(`${tableName}: Creating local item.`),
      localCreateEnd: () => console.info(`${tableName}: Local item created.`),
      localReadStart: () => console.info(`${tableName}: Reading local item(s)`),
      localReadEnd: () => console.info(`${tableName}: Local item(s) read.`),
      localUpdateStart: () => console.info(`${tableName}: Updating local item`),
      localUpdateEnd: () => console.info(`${tableName}: Local item updated.`),
      localDeleteStart: () => console.info(`${tableName}: Deleting local item`),
      localDeleteEnd: () => console.info(`${tableName}: Local item deleted.`),
      remoteCreateStart: () => console.info(`${tableName}: Creating local item.`),
      remoteCreateEnd: () => console.info(`${tableName}: Remote item created.`),
      remoteReadStart: () => console.info(`${tableName}: Reading remote item(s)`),
      remoteReadEnd: () => console.info(`${tableName}: Remote item(s) read.`),
      remoteUpdateStart: () => console.info(`${tableName}: Updating remote item`),
      remoteUpdateEnd: () => console.info(`${tableName}: Remote item updated.`),
      remoteDeleteStart: () => console.info(`${tableName}: Deleting remote item`),
      remoteDeleteEnd: () => console.info(`${tableName}: Remote item deleted.`),
      syncStart: () => console.info(`${tableName}: Deleting remote item`),
      syncEnd: () => console.info(`${tableName}: Remote item deleted.`),
    })(actions);

    datasourceActions.create();

    const isNoCalculated = val => !val.calculated;
    const isNoSelectOnly = val => !R.pathOr(false, ['selectOnly'])(val);
    const isSelectOnly = val => R.pathOr(false, ['selectOnly'])(val);
    const isNoAlter = val => !val.alter;
    const isAlter = val => val.alter;
    const idKey = schema.model.id || 'id';
    const schemaFields = R.pickBy(isNoCalculated)(schema.model.fields);
    const selectOnlyFields = R.pipe(
      R.pickBy(isSelectOnly),
      R.keys
    )(schema.model.fields);
    const updateFieldNotInSelectOnlyFields = field => R.indexOf(field, selectOnlyFields) === -1;
    const selectFields = R.pipe(
      R.keys,
      R.join(',')
    )(schemaFields);
    const schemaSelectFields = R.pipe(
      R.keys,
      R.filter(updateFieldNotInSelectOnlyFields),
      R.join(',')
    )(schemaFields);
    const updateFields = R.pipe(
      R.keys,
      R.filter(updateFieldNotInSelectOnlyFields),
      R.filter(k => k !== idKey),
      R.map(f => f + '=?'),
      R.join(',')
    );

    const createFields = R.pipe(
      R.pickBy(isNoSelectOnly),
      R.pickBy(isNoAlter),
      R.keys,
      R.map(key => (key === idKey ? `${key} STRING PRIMARY KEY` : key)),
      R.join(',')
    )(schemaFields);

    const auditFields = R.pipe(
      R.pickBy(isNoSelectOnly),
      R.pickBy(isNoAlter),
      R.keys,
      R.prepend('audit_id STRING PRIMARY KEY'),
      R.join(',')
    )(schemaFields);

    const dSource = {
      tableName,
      schema: {
        ...schema,
        model: {
          ...schema.model,
          id: idKey,
          auditable: undefined,
        },
      },
      auditable: schema.model.auditable,
      syncable: schema.model.syncable,
      autosync: !!schema.model.autoSync,
      fields: schemaFields,
      schemaSelectFields,
      selectFields,
      insertFields: schemaSelectFields,
      updateFields,
      createFields,
      auditFields,
    };

    const createTableSql = `CREATE TABLE IF NOT EXISTS ${dSource.tableName}(${dSource.createFields});`;

    const alterTables = () => {
      console.info(`${dSource.tableName}: created.`);

      const alterFields = R.pipe(
        R.pickBy(isNoSelectOnly),
        R.pickBy(isAlter),
        R.keys,
        R.map(key => ({
          sql: `ALTER TABLE ${dSource.tableName} ADD ${key};`,
        }))
      )(schemaFields);

      return executeDBSql({
        queue: alterFields,
      });
    };

    const syncDatasource = () =>
      new Promise(syncDatasourceSuccess => {
        console.log('syncDatasource');

        const {
          ajaxSession,
          getUserSessionUsername,
          getUserSessionUserData,
          getUserSessionForceLocalSync,
          checkOffline,
        } = api;

        const sync_call = username =>
          new Promise((syncCallSuccess, syncCallFail) => {
            const finish_sync = ({ data, syncData }) =>
              new Promise((finishSyncSuccess, finishSyncFail) => {
                console.log(`${tableName} -> syncData`, data.lastUpdate, syncData);
                // console.log(syncData);

                const lastUpdates = R.pipe(
                  R.mapObjIndexed((lastUpdate, proshop_id) => {
                    if (syncData[proshop_id]) {
                      const syncInfoSqlUpdate = `UPDATE synchro SET timestamp=${lastUpdate} WHERE table_name="${tableName}" AND username="${username}" AND proshop_id="${proshop_id}" AND type="GET"`;
                      return executeDBSql({ sql: syncInfoSqlUpdate, params: [] });
                    } else {
                      const syncInfoSqlInsert = `INSERT INTO synchro (id,timestamp,table_name,username,proshop_id, type) VALUES ("${uuid()}",${lastUpdate}, "${tableName}","${username}","${proshop_id}","GET")`;
                      return executeDBSql({ sql: syncInfoSqlInsert, params: [] });
                    }
                  }),
                  R.values
                )(data.lastUpdate);

                Promise.all(lastUpdates)
                  .then(finishSyncSuccess)
                  .catch(finishSyncFail);
              });

            const syncInfoSql = `SELECT * FROM synchro WHERE table_name="${tableName}" AND username="${username}"`;

            datasourceActions.localSyncStart({ tableName });
            executeDBSql({ sql: syncInfoSql, params: [] })
              .then(syncResult => {
                console.log(`${tableName} -> syncResult`, syncResult);
                const syncObject = {};
                let lastUpdateINS = 0;
                let lastUpdateUPD = 0;
                let lastUpdateDEL = 0;

                syncResult.forEach(obj => {
                  if (obj.type == 'GET') {
                    syncObject[obj.proshop_id] = obj.timestamp;
                  } else if (obj.type == 'INS') {
                    lastUpdateINS = obj.timestamp ? obj.timestamp : 0;
                  } else if (obj.type == 'UPD') {
                    lastUpdateUPD = obj.timestamp ? obj.timestamp : 0;
                  } else if (obj.type == 'DEL') {
                    lastUpdateDEL = obj.timestamp ? obj.timestamp : 0;
                  }
                });

                console.log(`${tableName} -> syncObject`, syncObject, lastUpdateINS, lastUpdateUPD, lastUpdateDEL);

                if (getUserSessionForceLocalSync()) {
                  lastUpdateINS = 0;
                  lastUpdateUPD = 0;
                  lastUpdateDEL = 0;
                }

                const lastUpdate = {};
                const userData = getUserSessionUserData();
                const { byProshop, byProshopSQL } = dSource.schema.model;

                console.log(`${tableName} -> userData`, userData);
                if (byProshop) {
                  userData.proshops.forEach(objProshop => {
                    if (objProshop.proshop) {
                      const proshopId = objProshop.proshop.id;
                      console.log(`${tableName} -> lastUpdate proshopId`, proshopId);
                      lastUpdate[proshopId] = syncObject[proshopId] ? syncObject[proshopId] : 0;
                      console.log(`${tableName} -> lastUpdate proshopId syncObj`, proshopId, syncObject[proshopId]);
                      console.log(`${tableName} -> lastUpdate proshopId lastUpdate`, proshopId, lastUpdate[proshopId]);
                    }
                  });
                } else {
                  lastUpdate.application = syncObject['application'] ? syncObject['application'] : 0;
                }

                console.log(`${tableName} -> syncObject`, syncObject);
                const syncLocalInfo = () =>
                  new Promise((syncLocalInfoSuccess, syncLocalInfoFail) => {
                    if (dSource.auditable) {
                      datasourceActions.localSyncProgress({ tableName, process: 'getLocalData', percentage: 0 });

                      const forceLocalSynch = getUserSessionForceLocalSync() ? '' : ' AND synched IS NULL ';

                      const getSyncTableData = () => {
                        // if (getUserSessionForceLocalSync()) {
                        //   //post table sync info
                        //   console.log(tableName);
                        //   const syncInsSql = `SELECT * FROM ${tableName}`;

                        //   return Promise.all([
                        //     new Promise((successForceSync, failForceSync) => {
                        //       executeDBSql({ sql: syncInsSql, params: [] })
                        //         .then(insResult => {
                        //           console.log('INSRESULT', insResult);
                        //           successForceSync(
                        //             R.map(insItem => R.merge(insItem, { revtype: 'INS', audit_id: insItem.id }))(
                        //               insResult
                        //             )
                        //           );
                        //         })
                        //         .catch(error => console.log('ERROR:', error) || failForceSync(error));
                        //     }),
                        //     Promise.resolve([]),
                        //     Promise.resolve([]),
                        //   ]);
                        // } else {
                        //post table sync info
                        console.log(tableName + '_audit');
                        const syncInsSql = `SELECT * FROM ${tableName}_audit WHERE updated_at>=${lastUpdateINS} AND revtype="INS" ${forceLocalSynch}`;
                        const syncUpdSql = `SELECT * FROM ${tableName}_audit WHERE updated_at>=${lastUpdateUPD} AND revtype="UPD" ${forceLocalSynch}`;
                        const syncDelSql = `SELECT * FROM ${tableName}_audit WHERE updated_at>=${lastUpdateDEL} AND revtype="DEL" ${forceLocalSynch}`;
                        console.log(syncInsSql, syncUpdSql, syncDelSql);

                        return Promise.all([
                          executeDBSql({ sql: syncInsSql, params: [] }),
                          executeDBSql({ sql: syncUpdSql, params: [] }),
                          executeDBSql({ sql: syncDelSql, params: [] }),
                        ]);
                        // }
                      };
                      const fixCreatedUpdatedBy = R.map(resultItem =>
                        R.pipe(
                          R.assoc('created_by', userData.id),
                          R.assoc('updated_by', userData.id)
                        )(resultItem)
                      );

                      const sendSyncData = ([insSqlResult, updSqlResult, delSqlResult]) => {
                        console.log(`${tableName} -> sync Local Info INS`, insSqlResult);
                        console.log(`${tableName} -> sync Local Info UPD`, updSqlResult);
                        console.log(`${tableName} -> sync Local Info DEL`, delSqlResult);

                        const bulkInsSqlResult = R.splitEvery(512)(insSqlResult);
                        const bulkUpdSqlResult = R.splitEvery(512)(updSqlResult);
                        const bulkDelSqlResult = R.splitEvery(512)(delSqlResult);

                        console.log(`${tableName} -> bulk sync Local Info INS`, bulkInsSqlResult);

                        const syncAjaxSession = (syncType, lastUpdate, data) =>
                          data.length > 0
                            ? ajaxSession(`sync/${tableName}`, {
                                type: syncType,
                                params: {
                                  lastUpdate,
                                },
                                data: fixCreatedUpdatedBy(data),
                              })
                            : Promise.resolve();

                        const bulkAjaxSession = (bulkData, syncType, lastUpdate) => {
                          return bulkData.reduce((p, data) => {
                            return p.then(() => syncAjaxSession(syncType, lastUpdate, data));
                          }, Promise.resolve()); // initial
                        };

                        const syncINSType = () => bulkAjaxSession(bulkInsSqlResult, 'POST', lastUpdateINS);

                        const syncUPDType = () => bulkAjaxSession(bulkUpdSqlResult, 'PUT', lastUpdateUPD);

                        const syncDELType = () => bulkAjaxSession(bulkDelSqlResult, 'DELETE', lastUpdateDEL);

                        return new Promise((successSyncTypes, failSyncTypes) =>
                          syncINSType()
                            .then(() => syncUPDType())
                            .then(() => syncDELType())
                            .then(() => successSyncTypes([insSqlResult, updSqlResult, delSqlResult]))
                            .catch(failSyncTypes)
                        );
                      };

                      const uploadLocalImages = ([insSqlResult, updSqlResult]) =>
                        new Promise(uploadLocalImagesSuccess => {
                          const { getImageDirectory, zipSyncImage, removeSyncZipFile } = fileSystem();
                          const { getSessionApiUrl } = api;

                          const results = [...insSqlResult, ...updSqlResult];

                          const getImages = specsheetId =>
                            new Promise((success, reject) => {
                              getImageDirectory()
                                .then(imageDirectoryReader => {
                                  imageDirectoryReader.createReader().readEntries(directoryEntries =>
                                    R.pipe(
                                      R.filter(file => file.name.indexOf(specsheetId) >= 0),
                                      success
                                    )(directoryEntries)
                                  );
                                })
                                .catch(reject);
                            });

                          const getCustomersImagesPath = () =>
                            new Promise(success =>
                              R.pipe(
                                R.map(customer =>
                                  customer.photo && customer.photo !== '' && customer.photo !== 'null'
                                    ? R.pipe(
                                        R.split('/'),
                                        R.last
                                      )(customer.photo)
                                    : ''
                                ),
                                R.without(['']),
                                R.dropRepeats,
                                success
                              )(results)
                            );

                          const getBallDrilledImagesPath = () =>
                            new Promise(success =>
                              R.pipe(
                                R.map(ballDrilled =>
                                  ballDrilled.photo && ballDrilled.photo !== '' && ballDrilled.photo !== 'null'
                                    ? R.pipe(
                                        R.split('/'),
                                        R.last
                                      )(ballDrilled.photo)
                                    : ''
                                ),
                                R.without(['']),
                                success
                              )(results)
                            );

                          const getSpecSheetImagesPath = () =>
                            new Promise(success =>
                              Promise.all(results.map(specSheet => getImages(specSheet.id)))
                                .then(paths =>
                                  success(
                                    R.pipe(
                                      R.flatten,
                                      R.map(path => path.name)
                                    )(paths)
                                  )
                                )
                                .catch(error => {
                                  console.log('error', error);
                                  success([]);
                                })
                            );

                          const getImagePaths = () =>
                            new Promise(success => {
                              switch (tableName) {
                                case 'customer':
                                  getCustomersImagesPath().then(success);
                                  break;
                                case 'ball_drilled':
                                  getBallDrilledImagesPath().then(success);
                                  break;
                                case 'spec_sheet':
                                  getSpecSheetImagesPath().then(success);
                                  break;
                                default:
                                  success([]);
                                  break;
                              }
                            });

                          const generateZipFile = imagePath => {
                            console.log('images paths revisited', imagePath);
                            return zipSyncImage(imagePath);
                          };

                          const sendImageToServer = zipImageFile => {
                            console.log('zipImagesFile', zipImageFile);
                            return R.pipeP(
                              () => getSessionApiUrl('images/uploadImage'),
                              url => {
                                console.log('url', url);
                                return sendData({ file: zipImageFile, url });
                              }
                            )();
                          };

                          const sendImagesToServer = imagePaths =>
                            new Promise(success => {
                              const totData = imagePaths.length;
                              let imagesSent = 0;

                              if (totData > 0) {
                                datasourceActions.localSyncProgress({
                                  tableName,
                                  process: 'uploadLocalImagesSent',
                                  percentage: 0,
                                });

                                datasourceActions.localSyncProgress({
                                  tableName,
                                  process: 'uploadLocalImagesProccessed',
                                  percentage: 0,
                                });
                              }

                              async.eachOfLimit(
                                imagePaths,
                                500,
                                (imagePath, imagePathIdx, done) => {
                                  const checkImagesSent = () => {
                                    imagesSent = imagesSent + 1;
                                    const percentage = ~~((imagesSent / totData) * 100);
                                    console.log(`images processed ${percentage} %`);
                                    datasourceActions.localSyncProgress({
                                      tableName,
                                      process: 'uploadLocalImagesProccessed',
                                      percentage,
                                    });
                                    done();
                                  };

                                  generateZipFile(imagePath).then(zipImageFile => {
                                    console.log('image zipped', zipImageFile);
                                    sendImageToServer(zipImageFile)
                                      .then(() => {
                                        checkImagesSent();
                                      })
                                      .catch(error => {
                                        console.warn(error);
                                        checkImagesSent();
                                      });
                                    const percentage = ~~(((imagePathIdx + 1) / totData) * 100);
                                    console.log(`sending images ${percentage} %`);
                                    datasourceActions.localSyncProgress({
                                      tableName,
                                      process: 'uploadLocalImagesSent',
                                      percentage,
                                    });
                                  });
                                },
                                () => {
                                  console.log(`images sent`);
                                  success();
                                }
                              );
                            });

                          const syncImages = R.pipeP(
                            getImagePaths,
                            sendImagesToServer
                          );

                          syncImages()
                            .then(() => uploadLocalImagesSuccess())
                            .catch(error => {
                              console.warn(error);
                              uploadLocalImagesSuccess();
                            });
                        });

                      const updateLocalSync = () => {
                        console.log(`${tableName} -> Update Local sync`);
                        const syncInfoSqlInsertUpdate = `UPDATE synchro SET timestamp=${now()} WHERE table_name="${tableName}" AND username="${username}" AND proshop_id="application" AND type="INS"`;
                        const syncInfoSqlInsertInsert = `INSERT INTO synchro (id,timestamp,table_name,username,proshop_id, type) VALUES ("${uuid()}",${now()}, "${tableName}","${username}","application","INS")`;
                        const syncInfoSqlInsertAudit = `UPDATE ${tableName}_audit SET synched="DONE" WHERE revtype="INS" ${forceLocalSynch}`;
                        const syncInfoSqlUpdateUpdate = `UPDATE synchro SET timestamp=${now()} WHERE table_name="${tableName}" AND username="${username}" AND proshop_id="application" AND type="UPD"`;
                        const syncInfoSqlUpdateInsert = `INSERT INTO synchro (id,timestamp,table_name,username,proshop_id, type) VALUES ("${uuid()}",${now()}, "${tableName}","${username}","application","UPD")`;
                        const syncInfoSqlUpdateAudit = `UPDATE ${tableName}_audit SET synched="DONE" WHERE revtype="UPD" ${forceLocalSynch}`;
                        const syncInfoSqlDeleteUpdate = `UPDATE synchro SET timestamp=${now()} WHERE table_name="${tableName}" AND username="${username}" AND proshop_id="application" AND type="DEL"`;
                        const syncInfoSqlDeleteInsert = `INSERT INTO synchro (id,timestamp,table_name,username,proshop_id, type) VALUES ("${uuid()}",${now()}, "${tableName}","${username}","application","DEL")`;
                        const syncInfoSqlDeleteAudit = `UPDATE ${tableName}_audit SET synched="DONE" WHERE revtype="DEL" ${forceLocalSynch}`;

                        return Promise.all([
                          executeDBSql({
                            sql: lastUpdateINS > 0 ? syncInfoSqlInsertUpdate : syncInfoSqlInsertInsert,
                            params: [],
                          }),
                          executeDBSql({ sql: syncInfoSqlInsertAudit, params: [] }),
                          executeDBSql({
                            sql: lastUpdateUPD > 0 ? syncInfoSqlUpdateUpdate : syncInfoSqlUpdateInsert,
                            params: [],
                          }),
                          executeDBSql({ sql: syncInfoSqlUpdateAudit, params: [] }),
                          executeDBSql({
                            sql: lastUpdateDEL > 0 ? syncInfoSqlDeleteUpdate : syncInfoSqlDeleteInsert,
                            params: [],
                          }),
                          executeDBSql({ sql: syncInfoSqlDeleteAudit, params: [] }),
                        ]);
                      };

                      R.pipeP(
                        getSyncTableData,
                        payload => {
                          datasourceActions.localSyncProgress({
                            tableName,
                            process: 'getLocalData',
                            percentage: 25,
                          });
                          return Promise.resolve(payload);
                        },
                        sendSyncData,
                        payload => {
                          datasourceActions.localSyncProgress({ tableName, process: 'getLocalData', percentage: 50 });
                          return Promise.resolve(payload);
                        },
                        uploadLocalImages,
                        payload => {
                          datasourceActions.localSyncProgress({ tableName, process: 'getLocalData', percentage: 75 });
                          return Promise.resolve(payload);
                        },
                        updateLocalSync
                      )()
                        .then(() => {
                          datasourceActions.localSyncProgress({
                            tableName,
                            process: 'getLocalData',
                            percentage: 100,
                          });
                          datasourceActions.localSyncEnd({
                            tableName,
                          });
                          syncLocalInfoSuccess();
                        })
                        .catch(error => {
                          console.warn(`${tableName} -> syncLocalDataError`, error);
                          syncLocalInfoFail(error);
                        });
                    } else {
                      syncLocalInfoSuccess();
                    }
                  });

                console.log(`${tableName} -> lastUpdate`, lastUpdate);
                //get table sync info
                datasourceActions.remoteSyncStart({
                  tableName,
                });

                return ajaxSession(`sync/${tableName}`, {
                  data: {
                    last_update: lastUpdate,
                  },
                }).then(getSyncData => {
                  let iData = 0;
                  let audit_entities = getSyncData.entities[tableName + '_audit'] || [];

                  const finishSyncRequest = (getSyncData, syncObject) => {
                    datasourceActions.remoteSyncEnd({
                      tableName,
                    });

                    return finish_sync({ data: getSyncData, syncData: syncObject })
                      .then(() => syncLocalInfo(getSyncData))
                      .then(syncCallSuccess)
                      .catch(error => {
                        console.log(`${tableName} -> finish syncRequest error:`, error);
                        syncCallSuccess();
                      });
                  };

                  if (audit_entities.length == 0) {
                    return finishSyncRequest(getSyncData, syncObject);
                  }

                  const totData = audit_entities.length;
                  let execQueue = [];

                  if (totData > 0) {
                    datasourceActions.remoteSyncProgress({
                      tableName,
                      process: 'processingSyncData',
                      percentage: 0,
                    });

                    datasourceActions.remoteSyncProgress({
                      tableName,
                      process: 'writingSyncData',
                      percentage: 0,
                    });
                  }

                  const checkSyncFinished = () => {
                    iData++;
                    const percentage = ~~(((iData + 1) / totData) * 100);
                    console.log(`${tableName} -> writing sync data ${percentage}%`);
                    datasourceActions.remoteSyncProgress({
                      tableName,
                      process: 'writingSyncData',
                      percentage,
                    });
                  };

                  async.eachOfLimit(
                    audit_entities,
                    500,
                    (modelData, modelIdx, done) => {
                      if (execQueue.length % 1000 == 0) {
                        console.log(`${tableName} -> commiting to local db`);
                        executeDBSql({ queue: execQueue });
                        execQueue = [];
                      }

                      const modelId = modelData[idKey];

                      if (modelData.revtype == 'INS') {
                        const model = R.pipe(
                          R.pick(R.keys(dSource.fields)),
                          R.mapObjIndexed((v, f) => {
                            if (f === 'updated_at' || f === 'created_at') {
                              return typeof v === 'string' ? moment(v).format('x') * 1 : v;
                            }
                            if (typeof v == 'string' && (v.toLowerCase() == 'undefined' || v.toLowerCase() == 'null')) {
                              return '';
                            }

                            if (dSource.fields[f].type == 'array' || dSource.fields[f].type == 'object') {
                              return JSON.stringify(v);
                            }

                            if (dSource.fields[f].type == 'boolean') {
                              return v.toString();
                            }

                            return v || '';
                          })
                        )(modelData);

                        const fields = R.join(',')(R.keys(model));
                        const values = R.values(model);
                        const params = R.join(',')(R.repeat('?', R.length(values)));

                        const sql = 'INSERT INTO ' + tableName + '(' + fields + ') VALUES(' + params + ')';
                        // console.log( sql );

                        execQueue.push({ sql, params: values, callback: checkSyncFinished });
                      } else if (modelData.revtype == 'UPD') {
                        const model = R.pipe(
                          R.pick(R.keys(dSource.fields)),
                          R.mapObjIndexed((v, f) => {
                            if (f === 'updated_at' || f === 'created_at') {
                              return typeof v === 'string' ? moment(v).format('x') * 1 : v;
                            }

                            if (typeof v == 'string' && (v.toLowerCase() == 'undefined' || v.toLowerCase() == 'null')) {
                              return '';
                            }

                            if (dSource.fields[f].type == 'array' || dSource.fields[f].type == 'object') {
                              return JSON.stringify(v);
                            }

                            if (dSource.fields[f].type == 'boolean') {
                              return v.toString();
                            }

                            return v || '';
                          })
                        )(modelData);

                        const fields = dSource.updateFields(model);
                        const values = R.pipe(
                          R.omit([idKey, ...selectOnlyFields]),
                          R.values,
                          R.append(modelId)
                        )(model);

                        if (fields) {
                          const sql = 'UPDATE ' + tableName + ' SET ' + fields + ' WHERE id=?';
                          // console.log( sql );
                          execQueue.push({ sql, params: values, callback: checkSyncFinished });
                        }
                      } else if (modelData.revtype == 'DEL') {
                        const values = [modelId];
                        const sql = 'DELETE FROM ' + tableName + ' WHERE id=?';
                        // console.log( sql );
                        execQueue.push({ sql, params: values, callback: checkSyncFinished });
                      }

                      const percentage = ~~(((modelIdx + 1) / totData) * 100);
                      console.log(`${tableName} -> processing sync data ${percentage} %`);
                      datasourceActions.remoteSyncProgress({
                        tableName,
                        process: 'processingSyncData',
                        percentage,
                      });

                      if (modelData.photo && modelData.photo !== '' && modelData.photo !== 'null') {
                        const file = R.pipe(
                          R.split('/'),
                          R.last
                        )(modelData.photo);
                        downloadData({
                          file,
                          url: `${apiConfig.appUrl}images/${api.getUserSessionUserData().id}/${file}`,
                        })
                          .then(() => done())
                          .catch(error => {
                            console.log(error);
                            done();
                          });
                      } else if (tableName === 'spec_sheet') {
                        const file = `${R.prop(idKey)(modelData)}.zip`;
                        downloadZipData({
                          file,
                          url: `${apiConfig.appUrl}images/${api.getUserSessionUserData().id}/${file}`,
                        })
                          .then(() => done())
                          .catch(error => {
                            console.log(error);
                            done();
                          });
                      } else {
                        done();
                      }
                    },
                    () => {
                      console.log(`${tableName} -> commiting to local db`);
                      executeDBSql({ queue: execQueue })
                        .then(() => finishSyncRequest(getSyncData, syncObject))
                        .catch(error => {
                          console.warn(error);
                          finishSyncRequest(getSyncData, syncObject);
                        });
                      execQueue = [];
                    }
                  );
                });
              })
              .catch(jqXHR => {
                //****** add alert message with the error  **********
                console.warn('error', jqXHR);

                syncCallFail();
              });
          });

        checkOffline()
          .then(offline => {
            if (offline) {
              console.log('offline?', offline);
              syncDatasourceSuccess();
            } else {
              const username = getUserSessionUsername();

              sync_call(username)
                .then(syncDatasourceSuccess)
                .catch(syncDatasourceSuccess);
            }
          })
          .catch(syncDatasourceSuccess);
      });

    dSource.sync = dSource.syncable ? syncDatasource : () => Promise.resolve();

    dSource.create = item =>
      new Promise((success, reject) => {
        const model = R.pipe(
          item => R.assocPath([idKey], R.pathOr(uuid(), ['localId'])(item))(item),
          R.omit(['localId']),
          item => {
            if (dSource.auditable) {
              return R.merge(item)({
                created_at: now(),
                updated_at: now(),
                created_by: dSource.fields.created_by ? currentUserId : undefined,
                updated_by: dSource.fields.updated_by ? currentUserId : undefined,
              });
            }

            return item;
          },
          R.pick(R.keys(dSource.fields)),
          R.mapObjIndexed((v, f) => {
            if (typeof v == 'string' && v.toLowerCase() == 'undefined') {
              return '';
            }

            if (dSource.fields[f].type == 'array' || dSource.fields[f].type == 'object') {
              return JSON.stringify(v);
            }

            if (dSource.fields[f].type == 'boolean') {
              return v.toString();
            }

            return v || '';
          })
        )(item);

        const fields = R.join(',')(R.keys(model));
        const valuesArray = R.values(model);
        const values = R.join(',')(R.repeat('?', R.length(valuesArray)));

        const sql = `INSERT INTO ${dSource.tableName}(${fields}) VALUES(${values});`;
        executeDBSql({ sql, params: valuesArray })
          .then(() => {
            success(R.pick(R.keys(dSource.fields))(model));
          })
          .catch(reject);

        if (dSource.auditable) {
          const valuesAuditArray = R.pipe(
            R.prepend(uuid()),
            R.append('INS')
          )(valuesArray);

          const sql_audit = `INSERT INTO ${dSource.tableName}_audit(audit_id,${fields},revtype) VALUES(?,${values},?)`;
          executeDBSql({
            sql: sql_audit,
            params: valuesAuditArray,
          }).then(() => {
            //dSource.sync()
            console.log('INSERTED!!!');
          });
        }
      });

    dSource.get = id =>
      new Promise((success, reject) =>
        dSource
          .read({ id })
          .then(result => success(result[0]))
          .catch(reject)
      );

    dSource.read = ({ id, proshopId = '', customerId = '', specsheetId = '' }) =>
      new Promise((success, reject) => {
        const { byProshop, byProshopSQL } = dSource.schema.model;

        const getSql = itemId => {
          if (itemId) {
            return `SELECT ${dSource.schemaSelectFields} FROM ${dSource.tableName} where ${idKey}="${itemId}"`;
          } else {
            if (byProshop) {
              if (byProshopSQL) {
                return byProshopSQL
                  .replace(/-tablename-/g, dSource.tableName)
                  .replace(/-proshopId-/g, proshopId)
                  .replace(/-customerId-/g, customerId)
                  .replace(/-specsheetId-/g, specsheetId);
              } else {
                if ('proshop_id' in dSource.fields) {
                  // return `SELECT ${dSource.schemaSelectFields} FROM ${dSource.tableName} WHERE proshop_id = "${proshopId}"`;
                  return `SELECT ${dSource.schemaSelectFields} FROM ${dSource.tableName}`;
                }
              }
            }
          }
          return `SELECT ${dSource.schemaSelectFields} FROM ${dSource.tableName}`;
        };

        const sql = getSql(id);

        datasourceActions.localReadStart();

        dSource.pictureCache = {};

        const readData = () => {
          console.log('reading data');
          R.pipeP(
            executeDBSql,
            result =>
              Promise.all(
                R.map(resultObj => proccessModelResultFields(dSource.schema.model, dSource.pictureCache)(resultObj))(
                  result
                )
              ),
            result => {
              datasourceActions.localReadEnd(result);
              return Promise.resolve(result);
            }
          )({ sql })
            .then(success)
            .catch(reject);
        };

        console.log('syncable', dSource.syncable);

        // dSource
        //   .sync()
        //   .then(readData)
        //   .catch(readData);
        readData();
      });

    dSource.update = item =>
      new Promise((success, reject) => {
        const model = R.pipe(
          R.omit(['localId']),
          item => {
            if (dSource.auditable) {
              console.log('auditable', item);
              return R.merge(item)({
                updated_at: now(),
                updated_by: dSource.fields.updated_by ? currentUserId : undefined,
              });
            }

            return item;
          },
          R.pick(R.keys(dSource.fields)),
          R.mapObjIndexed((v, f) => {
            if (typeof v == 'string' && v.toLowerCase() == 'undefined') {
              return '';
            }

            if (dSource.fields[f].type == 'array' || dSource.fields[f].type == 'object') {
              return JSON.stringify(v);
            }

            if (dSource.fields[f].type == 'boolean') {
              return v.toString();
            }

            return v || '';
          })
        )(item);

        const fields = dSource.updateFields(model);
        const valuesArray = R.pipe(
          R.omit([idKey, ...selectOnlyFields]),
          R.values,
          R.append(R.prop(idKey, model))
        )(model);

        const primaryKey = `${idKey} = ?`;
        const sql = `UPDATE ${dSource.tableName} SET ${fields} WHERE ${primaryKey}`;
        console.log(model, sql, valuesArray);
        executeDBSql({ sql, params: valuesArray })
          .then(resp => {
            success(model);
          })
          .catch(error => console.warn(error));

        if (dSource.auditable) {
          const valuesArrayAudit = R.pipe(
            R.split(','),
            fields => R.zipObj(fields)(R.repeat('', R.length(fields))),
            baseObj => R.merge(baseObj)(R.omit(selectOnlyFields)(model)),
            R.values,
            R.prepend(uuid()),
            R.append('UPD')
          )(dSource.insertFields);

          console.log(valuesArrayAudit);
          const values_audit = R.join(',')(R.repeat('?', R.length(valuesArrayAudit)));
          const sql_audit = `INSERT INTO ${dSource.tableName}_audit(audit_id,${
            dSource.insertFields
          },revtype) VALUES(${values_audit})`;
          executeDBSql({
            sql: sql_audit,
            params: valuesArrayAudit,
          }).then(() => {
            //dSource.sync()
            console.log('UPDATED!!!');
          });
        }
      });

    dSource.delete = itemId =>
      new Promise((success, reject) => {
        const primaryKey = `${idKey} = "${itemId}"`;
        const sql = `DELETE FROM ${dSource.tableName} WHERE ${primaryKey}`;
        executeDBSql({ sql }).then(() => {
          success();
        });
        if (dSource.auditable) {
          const valuesArray = [uuid(), itemId, now(), 'DEL'];
          var sql_audit = `INSERT INTO ${
            dSource.tableName
          }_audit(audit_id,${idKey},updated_at,revtype) VALUES(?,?,?,?)`;
          executeDBSql({ sql: sql_audit, params: valuesArray }).then(() => {
            //dSource.sync()
            console.log('DELETED!!!');
          });
        }
      });

    dSource.deleteFromFields = fields =>
      new Promise((success, reject) => {
        const where = Object.keys(fields)
          .map(field => `${field} = "${fields[field]}"`)
          .join(' AND ');
        const getRecordsToDelete = () => {
          const sql = `SELECT * FROM ${dSource.tableName} WHERE ${where}`;

          return executeDBSql({ sql });
        };

        const deleteRecords = () => {
          const sql = `DELETE FROM ${dSource.tableName} WHERE ${where}`;

          return executeDBSql({ sql }).then(() => {
            success();
          });
        };
        if (dSource.auditable) {
          getRecordsToDelete().then(records => {
            const deleteExecQueue = R.map(record => {
              const fieldsString = Object.keys(record).join(',');
              const valuesString = Object.keys(record)
                .map(() => '?')
                .join(',');
              const valuesArray = [uuid(), ...Object.values(record), now(), 'DEL'];
              var sql_audit = `INSERT INTO ${
                dSource.tableName
              }_audit(audit_id,${fieldsString},updated_at,revtype) VALUES(?,${valuesString},?,?)`;

              return { sql: sql_audit, params: valuesArray };
            })(records);

            executeDBSql({ queue: deleteExecQueue }).then(() => {
              // dSource.sync();
              console.log('BULK DELETED!!!');
              deleteRecords();
            });
          });
        } else {
          deleteRecords();
        }
      });

    const createAuditableTable = () => {
      console.info(`${dSource.tableName}: altered.`);

      if (dSource.auditable) {
        const createAuditTableSql = `CREATE TABLE IF NOT EXISTS ${dSource.tableName}_audit(${
          dSource.auditFields
        },revtype,synched);`;

        const alterAuditTables = () => {
          console.info(`${dSource.tableName}_audit: created.`);

          const auditAlterFields = R.pipe(
            R.pickBy(isAlter),
            R.keys,
            R.map(key => ({
              sql: `ALTER TABLE ${dSource.tableName}_audit ADD ${key};`,
            }))
          )(schemaFields);

          return executeDBSql({
            queue: auditAlterFields,
          });
        };

        return R.pipeP(
          executeDBSql,
          alterAuditTables
        )({
          sql: createAuditTableSql,
        });
      }

      return Promise.resolve();
    };

    R.pipeP(
      executeDBSql,
      alterTables
    )({
      sql: createTableSql,
    })
      .then(() => {
        console.info(`${dSource.tableName}_audit: altered.`);

        createAuditableTable()
          .then(() => {
            console.info(`Datasource ${dSource.tableName}: created.`);
            datasourceActions.created();

            datasourceSuccess(dSource);
          })
          .catch(error => {
            console.warn(`Error ${dSource.tableName}_audit: ${error}`);

            datasourceSuccess(dSource);
          });
      })
      .catch(error => {
        console.warn(`Error ${dSource.tableName}: ${error}`);

        createAuditableTable()
          .then(() => {
            console.info(`Datasource ${dSource.tableName}: created.`);
            datasourceActions.created();

            datasourceSuccess(dSource);
          })
          .catch(error => {
            console.warn(`Error ${dSource.tableName}_audit: ${error}`);

            datasourceSuccess(dSource);
          });
      });
  });
